import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    CardMedia,
    Chip,
    Button,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Category as CategoryIcon,
    AttachMoney as PriceIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

/**
 * Page de détails d'une relation User ↔ Listing
 */
const UserListingDetailPage = () => {
    const { userId, listingId } = useParams();
    const navigate = useNavigate();
    
    // États
    const [user, setUser] = useState(null);
    const [listing, setListing] = useState(null);
    const [userStats, setUserStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Charger les détails de l'utilisateur et de l'annonce
     */
    useEffect(() => {
        const loadDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                // Charger les détails de l'utilisateur et ses annonces
                const userResponse = await userListingService.getUserListingsByUserId(userId);
                if (userResponse.success) {
                    setUser(userResponse.user);
                    setUserStats(userResponse.statistics);
                    
                    // Trouver l'annonce spécifique
                    const targetListing = userResponse.listings.find(l => l.id === listingId);
                    if (targetListing) {
                        setListing(targetListing);
                    } else {
                        // Si pas trouvée, essayer de récupérer via l'API propriétaire
                        try {
                            const ownerResponse = await userListingService.getListingOwner(listingId);
                            if (ownerResponse.success) {
                                setListing(ownerResponse.listing);
                                setUser(ownerResponse.owner);
                                setUserStats(ownerResponse.statistics);
                            }
                        } catch (ownerError) {
                            setError('Annonce non trouvée');
                        }
                    }
                } else {
                    setError('Utilisateur non trouvé');
                }
            } catch (err) {
                console.error('Erreur lors du chargement:', err);
                setError('Erreur lors du chargement des détails');
            } finally {
                setLoading(false);
            }
        };

        if (userId && listingId) {
            loadDetails();
        } else {
            setError('Paramètres manquants');
            setLoading(false);
        }
    }, [userId, listingId]);

    /**
     * Formater la date
     */
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Obtenir la couleur du statut
     */
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'pending_verification':
                return 'warning';
            case 'rejected':
                return 'error';
            case 'inactive':
                return 'default';
            case 'draft':
                return 'info';
            case 'expired':
                return 'default';
            default:
                return 'primary';
        }
    };

    /**
     * Formater le prix
     */
    const formatPrice = (listing) => {
        if (!listing) return 'N/A';
        
        const prices = [];
        if (listing.price_hourly) prices.push(`${listing.price_hourly}${listing.currency || '€'}/h`);
        if (listing.price_daily) prices.push(`${listing.price_daily}${listing.currency || '€'}/j`);
        if (listing.price_weekly) prices.push(`${listing.price_weekly}${listing.currency || '€'}/sem`);
        if (listing.price_monthly) prices.push(`${listing.price_monthly}${listing.currency || '€'}/mois`);
        
        return prices.length > 0 ? prices.join(' • ') : 'Gratuit';
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="contained"
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/admin/user-listings')}
                >
                    Retour à la liste
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <PageHeader
                title="Détails de la Relation"
                subtitle={`${user?.name} ↔ ${listing?.title}`}
                icon={<BusinessIcon />}
            />

            {/* Boutons d'actions */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/admin/user-listings')}
                >
                    Retour à la liste
                </Button>
                <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/admin/user-listings/edit/${userId}/${listingId}`)}
                >
                    Modifier
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<EmailIcon />}
                    onClick={() => navigate(`/admin/user-listings/email?userId=${userId}&listingIds=${listingId}`)}
                >
                    Envoyer Email
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Informations utilisateur */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon color="primary" />
                            Informations Utilisateur
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar sx={{ width: 60, height: 60, mr: 2 }}>
                                {user?.name?.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography variant="h6">{user?.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Membre depuis: {formatDate(user?.memberSince || user?.createdAt)}
                                </Typography>
                            </Box>
                        </Box>

                        <List>
                            <ListItem>
                                <ListItemIcon><EmailIcon /></ListItemIcon>
                                <ListItemText primary={user?.email} />
                            </ListItem>
                            {user?.phone && (
                                <ListItem>
                                    <ListItemIcon><PhoneIcon /></ListItemIcon>
                                    <ListItemText primary={user?.phone} />
                                </ListItem>
                            )}
                        </List>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>Statut</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {user?.isOwner && <Chip label="Propriétaire" color="primary" size="small" />}
                            {user?.isTenant && <Chip label="Locataire" color="secondary" size="small" />}
                        </Box>

                        {/* Statistiques utilisateur */}
                        {userStats && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>Statistiques</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="h6" color="primary">
                                                {userStats.totalListings || 0}
                                            </Typography>
                                            <Typography variant="caption">Total Annonces</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="h6" color="success.main">
                                                {userStats.byStatus?.active || 0}
                                            </Typography>
                                            <Typography variant="caption">Actives</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* Informations annonce */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon color="primary" />
                            Informations Annonce
                        </Typography>

                        {/* Image principale */}
                        {listing?.images?.[0] && (
                            <CardMedia
                                component="img"
                                height="200"
                                image={listing.images[0].image_url}
                                alt={listing.title}
                                sx={{ borderRadius: 1, mb: 2 }}
                            />
                        )}

                        <Typography variant="h6" gutterBottom>{listing?.title}</Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                label={listing?.status}
                                color={getStatusColor(listing?.status)}
                                size="small"
                            />
                        </Box>

                        <List>
                            <ListItem>
                                <ListItemIcon><CategoryIcon /></ListItemIcon>
                                <ListItemText 
                                    primary="Catégorie" 
                                    secondary={listing?.category?.name || 'N/A'} 
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><PriceIcon /></ListItemIcon>
                                <ListItemText 
                                    primary="Prix" 
                                    secondary={formatPrice(listing)} 
                                />
                            </ListItem>
                            {listing?.address && (
                                <ListItem>
                                    <ListItemIcon><LocationIcon /></ListItemIcon>
                                    <ListItemText 
                                        primary="Adresse" 
                                        secondary={`${listing.address}, ${listing.city || ''}`} 
                                    />
                                </ListItem>
                            )}
                            <ListItem>
                                <ListItemIcon><ViewIcon /></ListItemIcon>
                                <ListItemText 
                                    primary="Vues" 
                                    secondary={listing?.viewCount || 0} 
                                />
                            </ListItem>
                        </List>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>Dates importantes</Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Créée:</strong> {formatDate(listing?.created_at)}
                        </Typography>
                        {listing?.validated_at && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Validée:</strong> {formatDate(listing.validated_at)}
                            </Typography>
                        )}

                        {listing?.description && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>Description</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {listing.description.substring(0, 200)}
                                    {listing.description.length > 200 && '...'}
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default UserListingDetailPage;
